/**
 * Состав забежки из прогресса игрока: герои (открытия, уровни и звёзды, купленные между волнами) и Tech. Одно правило
 * для сервера (S2 — после волны, S3 — после покупки и выхода) и клиента (I2 — состояние следующей волны без ожидания
 * ответа): иначе `WaveLog.start` клиента разойдётся с сервером и волна придёт как `corrected`.
 *
 * Платформы — как стояли, новый герой — на свою платформу по умолчанию ({@link defaultSlot}) или первую свободную.
 * В состав идут все открытые: после отката ниже волны открытия герой остаётся на поле (ISSUE-60).
 */
import type { BalanceData, HeroProgress, RunHero, RunState } from "@newfungame/contracts";
import { PLATFORM_COUNT } from "@newfungame/contracts";
import { betaRoster } from "../beta";
import { defaultSlot } from "../heroes/roster";
import { runHqMax } from "./run";

/** Часть прогресса героя, нужная составу. */
export type RosterHero = Pick<HeroProgress, "heroId" | "unlocked" | "level" | "stars">;

/**
 * `run` с героями и Tech из прогресса. Прочность штаба сдвигается на изменение максимума (купили Tech штаба —
 * прибавка сразу), но не выше нового максимума и не ниже 0. Волна, усиления, автонавыки и `runId` — из `run`.
 * Вход не мутируется; повторный вызов с тем же прогрессом ничего не меняет.
 */
export function refreshRun(
  balance: BalanceData,
  run: RunState,
  heroes: readonly RosterHero[],
  tech: Readonly<Record<string, number>>,
): RunState {
  if (balance.beta) {
    const next: RunState = {
      ...run,
      heroes: betaRoster(balance, run.heroes, heroes, tech),
      tech: { ...tech },
      boosts: run.boosts.map((x) => ({ ...x })),
    };
    const before = runHqMax(balance, run);
    const after = runHqMax(balance, next);
    next.hqHp = Math.max(0, Math.min(after, run.hqHp + after - before));
    return next;
  }
  const placed = new Map(run.heroes.map((h) => [h.heroId, h.slot]));
  const taken = new Set<number>();
  const roster: RunHero[] = [];
  const unlocked = heroes.filter((h) => h.unlocked);
  // сначала герои, что уже стоят на платформах, затем новые — на свою платформу или первую свободную
  for (const h of [...unlocked.filter((x) => placed.has(x.heroId)), ...unlocked.filter((x) => !placed.has(x.heroId))]) {
    let slot = placed.get(h.heroId) ?? defaultSlot(balance, h.heroId);
    if (taken.has(slot)) {
      slot = Array.from({ length: PLATFORM_COUNT }, (_, i) => i).find((i) => !taken.has(i)) ?? -1;
      if (slot < 0) continue;
    }
    taken.add(slot);
    roster.push({ heroId: h.heroId, slot, level: h.level, stars: h.stars });
  }
  roster.sort((x, y) => x.slot - y.slot);
  const next: RunState = { ...run, heroes: roster, tech: { ...tech }, boosts: run.boosts.map((x) => ({ ...x })) };
  const before = runHqMax(balance, run);
  const after = runHqMax(balance, next);
  next.hqHp = Math.max(0, Math.min(after, run.hqHp + after - before));
  return next;
}
